import React, { useState, useEffect, useMemo } from 'react';
import { Box, Typography, Modal, TextField, Button, Grid, MenuItem, IconButton, FormControl, InputLabel, Select, CircularProgress, Autocomplete, FormHelperText, Dialog, DialogContent } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import EditCalendarOutlinedIcon from '@mui/icons-material/EditCalendarOutlined';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import FileUploadDropzone from './FileUploadDropzone';
import { HousekeepingService } from '../api/services/housekeepingService';
import { RoomService } from '../api/services/roomService';
import { StaffService } from '../api/services/staffService';
import { InventoryService } from '../api/services/inventoryService';
import { useSelector } from 'react-redux';
import { toast } from '../utils/toast';

export default function TaskFormModal({ open, onClose, editingTask = null, onSuccess, availableStaff = [] }) {
    const { user } = useSelector(state => state.auth);
    const tenantId = user?.tenantId || '1';

    const userPermissions = useMemo(() => {
        if (!user) return [];
        let rawPerms = [];
        if (Array.isArray(user.customPermissions) && user.customPermissions.length > 0) {
            rawPerms = user.customPermissions;
        } else if (Array.isArray(user.permissions) && user.permissions.length > 0) {
            rawPerms = user.permissions;
        } else if (user.role && Array.isArray(user.role.permissions)) {
            rawPerms = user.role.permissions;
        }

        return rawPerms.map(p => {
            if (typeof p === 'string') return p;
            if (p && typeof p === 'object') {
                return p.key || p.name || p.code || '';
            }
            return '';
        }).filter(Boolean);
    }, [user]);

    const canManage = userPermissions.includes('*') || userPermissions.includes('CAN_MANAGE_HOUSEKEEPING');
    const canComplete = userPermissions.includes('*') || userPermissions.includes('CAN_COMPLETE_HS_TASK') || canManage;
    const isStatusAndProofOnly = !canManage && canComplete;

    const [formData, setFormData] = useState({
        roomId: '',
        taskTypeId: '',
        status: 'Ready',
        priority: 'Medium',
        assignedStaffId: '',
        dueBy: '',
        notes: '',
        cancellationReason: ''
    });

    const [mediaList, setMediaList] = useState([]);
    const [previewPopup, setPreviewPopup] = useState(null);
    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [rooms, setRooms] = useState([]);
    const [staff, setStaff] = useState([]);
    const [taskTypes, setTaskTypes] = useState([]);
    const [inventoryItems, setInventoryItems] = useState([]);
    const [consumedItems, setConsumedItems] = useState([]);
    const [isLoadingData, setIsLoadingData] = useState(false);

    useEffect(() => {
        if (open) {
            setConsumedItems([]);
            setErrors({});
            fetchDependencies();
            if (editingTask) {
                const staffObj = editingTask.assignedStaff;
                const staffName = staffObj?.fullName || staffObj?.name || (typeof editingTask.assignedTo === 'string' ? editingTask.assignedTo : '') || editingTask.assignedStaffName || '';
                const staffId = staffObj?.id || staffObj?._id || editingTask.assignedStaffId || (typeof editingTask.assignedTo === 'string' ? editingTask.assignedTo : '') || staffName || '';

                const rawMedia = editingTask.proofImages || editingTask.media || editingTask.attachments || editingTask.photos || editingTask.images || (editingTask.mediaUrl ? [editingTask.mediaUrl] : (editingTask.attachmentUrl ? [editingTask.attachmentUrl] : []));
                const parsedMedia = Array.isArray(rawMedia)
                    ? rawMedia.map(m => typeof m === 'string' ? m : (m?.url || m?.fileUrl || ''))
                    : (typeof rawMedia === 'string' && rawMedia ? [rawMedia] : []);
                setMediaList(parsedMedia.filter(Boolean));

                setFormData({
                    roomId: editingTask.roomId || '',
                    taskTypeId: editingTask.taskTypeId || '',
                    status: editingTask.status || 'Ready',
                    priority: editingTask.priority || 'Medium',
                    assignedStaffId: staffId,
                    dueBy: editingTask.dueBy ? new Date(editingTask.dueBy).toISOString().slice(0, 16) : '',
                    notes: editingTask.notes || '',
                    cancellationReason: editingTask.cancellationReason || ''
                });

                const initialStaff = [...(availableStaff || [])];
                if (staffName && !initialStaff.some(s => (s.id && s.id === staffId) || (s.fullName || s.name) === staffName)) {
                    initialStaff.push({
                        id: staffId || staffName,
                        fullName: staffName,
                        name: staffName
                    });
                }
                setStaff(initialStaff);
            } else {
                setMediaList([]);
                setFormData({
                    roomId: '',
                    taskTypeId: '',
                    status: 'Ready',
                    priority: 'Medium',
                    assignedStaffId: '',
                    dueBy: '',
                    notes: '',
                    cancellationReason: ''
                });
                setStaff(availableStaff || []);
            }
        }
    }, [open, editingTask]);

    const isLocked = editingTask && (editingTask.status === 'Completed' || editingTask.status === 'Cancelled');

    const getAvailableStatuses = () => {
        if (!editingTask) return ['Ready'];
        const current = editingTask.status || 'Ready';
        if (current === 'Ready') return ['Ready', 'In Progress', 'Cancelled'];
        if (current === 'In Progress') return ['In Progress', 'Inspection', 'Cancelled'];
        if (current === 'Inspection') return ['Inspection', 'Completed', 'In Progress', 'Cancelled'];
        if (current === 'Completed') return ['Completed'];
        if (current === 'Cancelled') return ['Cancelled'];
        return ['Ready', 'In Progress', 'Inspection', 'Completed', 'Cancelled'];
    };

    const fetchDependencies = async () => {
        setIsLoadingData(true);
        try {
            if (!canManage) {
                const initialStaff = [...(availableStaff || [])];
                if (editingTask) {
                    const staffObj = editingTask.assignedStaff;
                    const staffName = staffObj?.fullName || staffObj?.name || (typeof editingTask.assignedTo === 'string' ? editingTask.assignedTo : '') || editingTask.assignedStaffName || '';
                    const staffId = staffObj?.id || staffObj?._id || editingTask.assignedStaffId || (typeof editingTask.assignedTo === 'string' ? editingTask.assignedTo : '') || staffName || '';
                    if (staffName && !initialStaff.some(s => (s.id && s.id === staffId) || (s.fullName || s.name) === staffName)) {
                        initialStaff.push({
                            id: staffId || staffName,
                            fullName: staffName,
                            name: staffName
                        });
                    }
                }
                setStaff(initialStaff);

                const typesRes = await HousekeepingService.getTaskTypes(tenantId).catch(() => []);
                const typesData = Array.isArray(typesRes) ? typesRes : (typesRes?.data || []);
                setTaskTypes(typesData);
                return;
            }

            const [roomsRes, staffRes, typesRes, inventoryRes] = await Promise.all([
                RoomService.getAll({ noPagination: true }, tenantId).catch(err => {
                    console.warn("RoomService.getAll skipped/failed:", err);
                    return [];
                }),
                StaffService.getStaff({ noPagination: true }).catch(err => {
                    console.warn("StaffService.getStaff skipped/failed:", err);
                    return [];
                }),
                HousekeepingService.getTaskTypes(tenantId).catch(err => {
                    console.warn("getTaskTypes skipped/failed:", err);
                    return [];
                }),
                InventoryService.getInventoryItems().catch(err => {
                    console.warn("InventoryService.getInventoryItems skipped/failed:", err);
                    return [];
                })
            ]);
            
            const roomData = Array.isArray(roomsRes) ? roomsRes : (roomsRes?.data || []);
            const staffList = Array.isArray(staffRes)
                ? staffRes
                : (Array.isArray(staffRes?.data)
                    ? staffRes.data
                    : (Array.isArray(staffRes?.staff)
                        ? staffRes.staff
                        : (Array.isArray(staffRes?.data?.staff)
                            ? staffRes.data.staff
                            : (Array.isArray(staffRes?.staffMembers)
                                ? staffRes.staffMembers
                                : []))));
            const typesData = Array.isArray(typesRes) ? typesRes : (typesRes?.data || []);
            const inventoryData = Array.isArray(inventoryRes) ? inventoryRes : (inventoryRes?.data || inventoryRes?.items || []);
            
            const mergedStaff = [...(staffList.filter(s => !s.status || s.status.toLowerCase() === 'active'))];
            (availableStaff || []).forEach(s => {
                if (s && !mergedStaff.some(item => (item.id && item.id === s.id) || (item.fullName || item.name) === (s.fullName || s.name))) {
                    mergedStaff.push(s);
                }
            });
            if (editingTask) {
                const staffObj = editingTask.assignedStaff;
                const staffName = staffObj?.fullName || staffObj?.name || (typeof editingTask.assignedTo === 'string' ? editingTask.assignedTo : '') || editingTask.assignedStaffName || '';
                const staffId = staffObj?.id || staffObj?._id || editingTask.assignedStaffId || (typeof editingTask.assignedTo === 'string' ? editingTask.assignedTo : '') || staffName || '';
                if (staffName && !mergedStaff.some(s => (s.id && s.id === staffId) || (s.fullName || s.name) === staffName)) {
                    mergedStaff.push({
                        id: staffId || staffName,
                        fullName: staffName,
                        name: staffName
                    });
                }
            }

            setRooms(roomData);
            setStaff(mergedStaff);
            setTaskTypes(typesData);
            setInventoryItems(inventoryData);
        } catch (error) {
            console.error('Failed to fetch dependencies:', error);
        } finally {
            setIsLoadingData(false);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: null }));
        }
        
        if (name === 'status' && value !== 'Completed') {
            setConsumedItems([]);
        }
    };

    const handleSubmit = async () => {
        if (!editingTask && !canManage) {
            toast.error("You do not have permission to create housekeeping tasks");
            return;
        }
        if (editingTask && !canManage && !canComplete) {
            toast.error("You do not have permission to update housekeeping tasks");
            return;
        }

        const newErrors = {};
        if (!editingTask) {
            if (!formData.roomId) newErrors.roomId = "Room is required";
            if (!formData.taskTypeId) newErrors.taskTypeId = "Task type is required";
            if (!formData.priority) newErrors.priority = "Priority is required";
            if (!formData.assignedStaffId) newErrors.assignedStaffId = "Staff assignment is required";
            if (!formData.dueBy) {
                newErrors.dueBy = "Due date & time is required";
            } else if (isNaN(new Date(formData.dueBy).getTime())) {
                newErrors.dueBy = "Invalid due date & time";
            }
        } else if (canManage) {
            if (!formData.priority) newErrors.priority = "Priority is required";
            if (!formData.assignedStaffId) newErrors.assignedStaffId = "Staff assignment is required";
            if (!formData.dueBy) {
                newErrors.dueBy = "Due date & time is required";
            } else if (isNaN(new Date(formData.dueBy).getTime())) {
                newErrors.dueBy = "Invalid due date & time";
            }
        }

        if (formData.status === 'Cancelled' && !formData.cancellationReason) {
            newErrors.cancellationReason = "Cancellation reason is required";
        }

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            toast.error(newErrors.assignedStaffId || "Please fill all required fields");
            return;
        }

        setIsSubmitting(true);
        try {
            if (editingTask) {
                let payload = {};
                if (isStatusAndProofOnly) {
                    payload = {
                        status: formData.status,
                        proofImages: mediaList
                    };
                    if (formData.notes !== undefined && formData.notes !== null) {
                        payload.notes = formData.notes;
                    }
                    if (formData.status === 'Cancelled' && formData.cancellationReason) {
                        payload.cancellationReason = formData.cancellationReason;
                    }
                    if (formData.status === 'Completed' && consumedItems.length > 0) {
                        const validItems = consumedItems.filter(item => item.itemId && item.quantity > 0);
                        if (validItems.length > 0) {
                            payload.consumedItems = validItems;
                        }
                    }
                } else {
                    payload = {
                        status: formData.status,
                        priority: formData.priority,
                        assignedStaffId: formData.assignedStaffId,
                        dueBy: formData.dueBy ? new Date(formData.dueBy).toISOString() : undefined,
                        notes: formData.notes,
                        proofImages: mediaList
                    };
                    if (formData.status === 'Cancelled') {
                        payload.cancellationReason = formData.cancellationReason;
                    }
                    if (formData.status === 'Completed' && consumedItems.length > 0) {
                        const validItems = consumedItems.filter(item => item.itemId && item.quantity > 0);
                        if (validItems.length > 0) {
                            payload.consumedItems = validItems;
                        }
                    }
                }
                await HousekeepingService.updateTask(editingTask.id, payload, tenantId);
                toast.success("Housekeeping task updated successfully");
            } else {
                const payload = {
                    roomId: formData.roomId,
                    taskTypeId: formData.taskTypeId,
                    priority: formData.priority,
                    assignedStaffId: formData.assignedStaffId,
                    dueBy: new Date(formData.dueBy).toISOString(),
                    notes: formData.notes
                };
                await HousekeepingService.createTask(payload, tenantId);
                toast.success("Housekeeping task successfully scheduled and assigned");
            }
            
            if (onSuccess) onSuccess();
            onClose();
            
        } catch (error) {
            console.error("Submission error:", error);
            toast.error(error.message || "Failed to process housekeeping task");
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleModalClose = (event, reason) => {
        if (reason === 'backdropClick') return;
        if (isSubmitting) return;
        onClose();
    };

    return (
        <>
            <Modal open={open} onClose={handleModalClose}>
                <Box sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: { xs: '92%', sm: 580 },
                    maxHeight: '90vh',
                    display: 'flex',
                    flexDirection: 'column',
                    bgcolor: '#ffffff',
                    borderRadius: 4,
                    boxShadow: '0 24px 48px rgba(0,0,0,0.14)',
                    border: '1px solid rgba(0,0,0,0.06)',
                    outline: 'none',
                    overflow: 'hidden',
                }}>
                    {/* Header */}
                    <Box sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        p: { xs: 2.5, sm: '20px 28px' },
                        borderBottom: '1px solid #f1f5f9',
                        flexShrink: 0,
                    }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Box sx={{
                                width: 42,
                                height: 42,
                                borderRadius: '12px',
                                bgcolor: 'rgba(25, 118, 210, 0.08)',
                                color: 'primary.main',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}>
                                {editingTask ? <EditCalendarOutlinedIcon /> : <AddCircleOutlineIcon />}
                            </Box>
                            <Box>
                                <Typography variant="h6" fontWeight="700" sx={{ lineHeight: 1.2 }}>
                                    {editingTask 
                                        ? (isStatusAndProofOnly ? 'Update Task Status & Proof' : 'Edit Housekeeping Task') 
                                        : 'Create Housekeeping Task'}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    {editingTask 
                                        ? (isStatusAndProofOnly ? 'Update task progress and upload proof photos' : 'Modify housekeeping schedule and assignment') 
                                        : 'Schedule a new room cleaning task'}
                                </Typography>
                            </Box>
                        </Box>
                        <IconButton
                            onClick={onClose}
                            size="small"
                            disabled={isSubmitting}
                            sx={{
                                color: 'text.secondary',
                                bgcolor: 'rgba(0,0,0,0.04)',
                                '&:hover': { bgcolor: 'rgba(0,0,0,0.08)' },
                            }}
                        >
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </Box>

                    {/* Scrollable Body */}
                    <Box sx={{
                        p: { xs: 2.5, sm: '22px 28px' },
                        overflowY: 'auto',
                        flex: 1,
                        '&::-webkit-scrollbar': {
                            width: '6px',
                        },
                        '&::-webkit-scrollbar-track': {
                            background: 'transparent',
                        },
                        '&::-webkit-scrollbar-thumb': {
                            background: '#cbd5e1',
                            borderRadius: '3px',
                        },
                        '&::-webkit-scrollbar-thumb:hover': {
                            background: '#94a3b8',
                        },
                    }}>
                        {/* Modern Soft Alert for Locked Tasks */}
                        {isLocked && (
                            <Box sx={{
                                mb: 3,
                                p: 2,
                                bgcolor: '#fef2f2',
                                border: '1px solid #fecaca',
                                borderRadius: 2.5,
                                display: 'flex',
                                alignItems: 'flex-start',
                                gap: 1.5,
                            }}>
                                <LockOutlinedIcon sx={{ color: '#ef4444', fontSize: 20, mt: 0.2 }} />
                                <Box>
                                    <Typography variant="caption" fontWeight="700" color="#991b1b" sx={{ textTransform: 'uppercase', letterSpacing: '0.4px', display: 'block' }}>
                                        Task Locked ({editingTask.status})
                                    </Typography>
                                    <Typography variant="body2" color="#b91c1c" sx={{ mt: 0.25, lineHeight: 1.4 }}>
                                        This task is permanently locked and cannot be changed. If you made a mistake, you must create a new task.
                                    </Typography>
                                </Box>
                            </Box>
                        )}

                        {isLoadingData ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                                <CircularProgress />
                            </Box>
                        ) : (
                            <Grid container spacing={2}>
                            {!editingTask && (
                                <>
                                    <Grid item xs={12} sm={6}>
                                        <Autocomplete
                                            options={rooms}
                                            getOptionLabel={(option) => option.roomNumber ? `${option.roomNumber} (${option.type || option.roomType?.name || 'Room'})` : option.name || option.number || option.id}
                                            value={rooms.find(r => r.id === formData.roomId) || null}
                                            onChange={(event, newValue) => {
                                                setFormData(prev => ({ ...prev, roomId: newValue ? newValue.id : '' }));
                                                if (errors.roomId) setErrors(prev => ({ ...prev, roomId: null }));
                                            }}
                                            disabled={!canManage}
                                            renderInput={(params) => (
                                                <TextField 
                                                    {...params} 
                                                    label="Room " 
                                                    required 
                                                    size="small" 
                                                    disabled={!canManage}
                                                    error={!!errors.roomId}
                                                    helperText={errors.roomId}
                                                />
                                            )}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <FormControl fullWidth size="small" required disabled={!canManage} error={!!errors.taskTypeId}>
                                            <InputLabel>Task Type </InputLabel>
                                            <Select name="taskTypeId" value={formData.taskTypeId} label="Task Type *" onChange={handleChange}>
                                                {taskTypes.map(t => (
                                                    <MenuItem key={t.id || t._id} value={t.id || t._id}>{t.name}</MenuItem>
                                                ))}
                                            </Select>
                                            {errors.taskTypeId && <FormHelperText>{errors.taskTypeId}</FormHelperText>}
                                        </FormControl>
                                    </Grid>
                                </>
                            )}
                            {editingTask && (
                                <Grid item xs={12}>
                                    <FormControl fullWidth size="small" required disabled={isLocked || (!canManage && !canComplete)}>
                                        <InputLabel>Status</InputLabel>
                                        <Select name="status" value={formData.status} label="Status" onChange={handleChange}>
                                            {getAvailableStatuses().map(st => (
                                                <MenuItem key={st} value={st}>{st}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                            )}
                            <Grid item xs={12} sm={6}>
                                <FormControl fullWidth size="small" required disabled={isLocked || isStatusAndProofOnly || (!canManage && !canComplete)} error={!!errors.priority}>
                                    <InputLabel>Priority </InputLabel>
                                    <Select name="priority" value={formData.priority} label="Priority *" onChange={handleChange}>
                                        <MenuItem value="Low">Low</MenuItem>
                                        <MenuItem value="Medium">Medium</MenuItem>
                                        <MenuItem value="High">High</MenuItem>
                                        <MenuItem value="Urgent">Urgent</MenuItem>
                                    </Select>
                                    {errors.priority && <FormHelperText>{errors.priority}</FormHelperText>}
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <FormControl fullWidth size="small" required disabled={isLocked || isStatusAndProofOnly || (!canManage && !canComplete)} error={!!errors.assignedStaffId}>
                                    <InputLabel>Assign Staff </InputLabel>
                                    <Select name="assignedStaffId" value={formData.assignedStaffId} label="Assign Staff *" onChange={handleChange}>
                                        {staff.map(s => {
                                            const val = s.id || s._id || s.fullName || s.name;
                                            const label = s.fullName || s.name || `${s.firstName || ''} ${s.lastName || ''}`.trim() || s.email || val;
                                            return (
                                                <MenuItem key={val} value={val}>
                                                    {label}
                                                </MenuItem>
                                            );
                                        })}
                                        {staff.length === 0 && <MenuItem disabled>No housekeeping staff available</MenuItem>}
                                    </Select>
                                    {errors.assignedStaffId && <FormHelperText>{errors.assignedStaffId}</FormHelperText>}
                                </FormControl>
                            </Grid>
                            <Grid item xs={12}>
                                <TextField 
                                    fullWidth 
                                    label="Due Date & Time " 
                                    type="datetime-local" 
                                    name="dueBy" 
                                    value={formData.dueBy} 
                                    onChange={handleChange} 
                                    InputLabelProps={{ shrink: true }} 
                                    inputProps={{
                                        min: new Date().toISOString().slice(0, 16)
                                    }}
                                    size="small"
                                    required
                                    disabled={isLocked || isStatusAndProofOnly || (!canManage && !canComplete)}
                                    error={!!errors.dueBy}
                                    helperText={errors.dueBy}
                                />
                            </Grid>
                        {formData.status === 'Cancelled' && (
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    required
                                    label="Cancellation Reason "
                                    name="cancellationReason"
                                    value={formData.cancellationReason}
                                    onChange={handleChange}
                                    disabled={isLocked}
                                    placeholder="Enter cancellation reason"
                                    error={!!errors.cancellationReason}
                                    helperText={errors.cancellationReason}
                                />
                            </Grid>
                        )}
                        {formData.status === 'Completed' && (
                            <Grid item xs={12}>
                                <Box sx={{ p: 2, bgcolor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 2.5 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                                        <Inventory2OutlinedIcon sx={{ fontSize: 18, color: '#64748b' }} />
                                        <Typography variant="caption" fontWeight="700" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: '0.4px' }}>
                                            Inventory Consumed (Optional)
                                        </Typography>
                                    </Box>
                                    {consumedItems.map((item, index) => (
                                        <Box key={index} sx={{ display: 'flex', gap: 1.5, mb: 1.5, alignItems: 'center' }}>
                                            <FormControl size="small" fullWidth disabled={isLocked}>
                                                <InputLabel>Item Used</InputLabel>
                                                <Select
                                                    value={item.itemId}
                                                    label="Item Used"
                                                    onChange={(e) => {
                                                        const newConsumed = [...consumedItems];
                                                        newConsumed[index].itemId = e.target.value;
                                                        setConsumedItems(newConsumed);
                                                    }}
                                                >
                                                    {inventoryItems.map(invItem => (
                                                        <MenuItem key={invItem.id || invItem._id} value={invItem.id || invItem._id}>{invItem.name}</MenuItem>
                                                    ))}
                                                    {inventoryItems.length === 0 && <MenuItem disabled>No items available</MenuItem>}
                                                </Select>
                                            </FormControl>
                                            <TextField 
                                                size="small" 
                                                label="Qty" 
                                                type="number" 
                                                InputProps={{ inputProps: { min: 1 } }}
                                                sx={{ width: 100 }}
                                                value={item.quantity}
                                                onChange={(e) => {
                                                    const newConsumed = [...consumedItems];
                                                    newConsumed[index].quantity = parseInt(e.target.value, 10) || '';
                                                    setConsumedItems(newConsumed);
                                                }}
                                                disabled={isLocked}
                                            />
                                            <IconButton color="error" size="small" disabled={isLocked} onClick={() => {
                                                const newConsumed = [...consumedItems];
                                                newConsumed.splice(index, 1);
                                                setConsumedItems(newConsumed);
                                            }}>
                                                <CloseIcon fontSize="small" />
                                            </IconButton>
                                        </Box>
                                    ))}
                                    <Button size="small" variant="outlined" disabled={isLocked} onClick={() => setConsumedItems([...consumedItems, { itemId: '', quantity: 1 }])} sx={{ textTransform: 'none', borderRadius: 2 }}>
                                        + Log Item Usage
                                    </Button>
                                </Box>
                            </Grid>
                        )}
                        <Grid item xs={12}>
                            <TextField 
                                fullWidth 
                                label="Internal Notes" 
                                name="notes" 
                                multiline 
                                rows={3} 
                                value={formData.notes} 
                                onChange={handleChange} 
                                size="small"
                                disabled={isLocked}
                                placeholder="Additional details or special instructions..."
                            />
                        </Grid>

                        {/* ── Media & Attachments (Only for Edit & Status Update) ──────────────────────────── */}
                        {editingTask && (
                            <Grid item xs={12}>
                                <Box sx={{ p: 2, bgcolor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 2.5 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <AttachFileIcon sx={{ fontSize: 18, color: '#64748b' }} />
                                            <Typography variant="caption" fontWeight="700" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: '0.4px' }}>
                                                Proof Images & Attachments
                                            </Typography>
                                        </Box>
                                        {mediaList.length > 0 && (
                                            <Typography variant="caption" color="text.secondary" fontWeight="600">
                                                {mediaList.length} file{mediaList.length > 1 ? 's' : ''} attached
                                            </Typography>
                                        )}
                                    </Box>

                                    {/* Existing / Uploaded Media List */}
                                    {mediaList.length > 0 && (
                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mb: 2 }}>
                                            {mediaList.map((url, idx) => {
                                                const isImg = typeof url === 'string' && (/\.(jpeg|jpg|gif|png|webp|svg)($|\?)/i.test(url) || url.startsWith('data:image'));
                                                const fileName = typeof url === 'string' ? url.split('/').pop().split('?')[0] : `Attachment ${idx + 1}`;
                                                return (
                                                    <Box key={idx} sx={{
                                                        position: 'relative',
                                                        width: 100,
                                                        height: 100,
                                                        borderRadius: 2,
                                                        border: '1px solid #e2e8f0',
                                                        overflow: 'hidden',
                                                        bgcolor: '#fff',
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        boxShadow: '0 2px 4px rgba(0,0,0,0.04)',
                                                        transition: 'all 0.2s',
                                                        '&:hover .media-actions': { opacity: 1 }
                                                    }}>
                                                        {isImg ? (
                                                            <Box
                                                                component="img"
                                                                src={url}
                                                                alt={fileName}
                                                                sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                                            />
                                                        ) : (
                                                            <Box sx={{ p: 1, textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                                                <InsertDriveFileOutlinedIcon sx={{ fontSize: 32, color: '#64748b', mb: 0.5 }} />
                                                                <Typography variant="caption" noWrap sx={{ maxWidth: 80, fontSize: '0.65rem', color: '#475569' }}>
                                                                    {fileName}
                                                                </Typography>
                                                            </Box>
                                                        )}

                                                        {/* Hover overlay actions */}
                                                        <Box className="media-actions" sx={{
                                                            position: 'absolute',
                                                            inset: 0,
                                                            bgcolor: 'rgba(0,0,0,0.5)',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            gap: 0.5,
                                                            opacity: 0,
                                                            transition: 'opacity 0.2s',
                                                        }}>
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => isImg ? setPreviewPopup(url) : window.open(url, '_blank')}
                                                                sx={{ color: '#fff', bgcolor: 'rgba(255,255,255,0.2)', '&:hover': { bgcolor: 'rgba(255,255,255,0.4)' } }}
                                                                title="View"
                                                            >
                                                                <VisibilityOutlinedIcon fontSize="small" />
                                                            </IconButton>
                                                            {!isLocked && (
                                                                <IconButton
                                                                    size="small"
                                                                    onClick={() => setMediaList(prev => prev.filter((_, i) => i !== idx))}
                                                                    sx={{ color: '#ff4d4f', bgcolor: 'rgba(255,255,255,0.2)', '&:hover': { bgcolor: 'rgba(255,255,255,0.4)' } }}
                                                                    title="Delete"
                                                                >
                                                                    <DeleteOutlineIcon fontSize="small" />
                                                                </IconButton>
                                                            )}
                                                        </Box>
                                                    </Box>
                                                );
                                            })}
                                        </Box>
                                    )}

                                    {/* Upload Dropzone */}
                                    {!isLocked && (
                                        <FileUploadDropzone
                                            multiple={true}
                                            accept="image/*,application/pdf"
                                            maxSizeMB={10}
                                            onUploadSuccess={(url) => {
                                                if (url) {
                                                    setMediaList(prev => [...prev, url]);
                                                }
                                            }}
                                            onMultipleUploadSuccess={(urls) => {
                                                if (urls && urls.length > 0) {
                                                    setMediaList(prev => {
                                                        const combined = [...prev];
                                                        urls.forEach(u => {
                                                            if (u && !combined.includes(u)) combined.push(u);
                                                        });
                                                        return combined;
                                                    });
                                                }
                                            }}
                                        />
                                    )}
                                </Box>
                            </Grid>
                        )}
                    </Grid>
                )}
                    </Box>

                    {/* Fixed Footer Buttons */}
                    <Box sx={{
                        display: 'flex',
                        gap: 2,
                        p: { xs: 2, sm: '16px 28px 20px 28px' },
                        borderTop: '1px solid #f1f5f9',
                        bgcolor: '#ffffff',
                        flexShrink: 0,
                    }}>
                        <Button
                            fullWidth
                            variant="outlined"
                            onClick={onClose}
                            disabled={isSubmitting}
                            sx={{
                                py: 1.1,
                                borderRadius: 2.5,
                                textTransform: 'none',
                                fontWeight: '600',
                                color: '#475569',
                                borderColor: '#cbd5e1',
                                '&:hover': { borderColor: '#94a3b8', bgcolor: '#f8fafc' },
                            }}
                        >
                            Close
                        </Button>
                        {!isLocked && (canManage || canComplete) && (!(!editingTask && !canManage)) && (
                            <Button 
                                fullWidth 
                                variant="contained" 
                                onClick={handleSubmit} 
                                disabled={isSubmitting || isLoadingData}
                                startIcon={isSubmitting ? <CircularProgress size={16} color="inherit" /> : null}
                                sx={{
                                    py: 1.1,
                                    borderRadius: 2.5,
                                    textTransform: 'none',
                                    fontWeight: '700',
                                    boxShadow: 'none',
                                    '&:hover': { boxShadow: '0 4px 12px rgba(25, 118, 210, 0.24)' },
                                }}
                            >
                                {isSubmitting ? 'Saving...' : (editingTask ? (isStatusAndProofOnly ? 'Update Status & Proof' : 'Update Task') : 'Save Task')}
                            </Button>
                        )}
                    </Box>
                </Box>
            </Modal>

            {/* Media Image Preview Dialog */}
            <Dialog open={!!previewPopup} onClose={() => setPreviewPopup(null)} maxWidth="md">
                <DialogContent sx={{ p: 1, position: 'relative', bgcolor: '#000', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    <IconButton
                        onClick={() => setPreviewPopup(null)}
                        sx={{ position: 'absolute', top: 8, right: 8, color: '#fff', bgcolor: 'rgba(0,0,0,0.5)', '&:hover': { bgcolor: 'rgba(0,0,0,0.8)' } }}
                    >
                        <CloseIcon />
                    </IconButton>
                    {previewPopup && (
                        <Box component="img" src={previewPopup} alt="Preview" sx={{ maxWidth: '100%', maxHeight: '80vh', objectFit: 'contain' }} />
                    )}
                </DialogContent>
            </Dialog>
        </>
    );
}
